/**
 * IndexedDB access.
 *
 * Hand-rolled rather than pulled from a library, because the surface this app
 * needs is small and the dependency-free data layer is the thing that has to
 * keep working offline for years. Every call returns a promise; every write
 * goes through a transaction that is awaited to completion (not merely to
 * request success), so a caller that awaits a put knows the data is durable.
 */
import { SCHEMA_VERSION, type StoreMap, type StoreName } from './schema';

const DB_NAME = 'reta';

/** Index definitions per store, created on upgrade. */
const INDEXES: Record<string, { name: string; keyPath: string | string[]; unique?: boolean }[]> = {
  foods: [
    { name: 'by_name', keyPath: 'name' },
    { name: 'by_barcode', keyPath: 'barcode' },
    { name: 'by_useCount', keyPath: 'useCount' },
  ],
  foodLogs: [{ name: 'by_day', keyPath: 'day' }],
  sessions: [{ name: 'by_day', keyPath: 'day' }],
  runs: [
    { name: 'by_day', keyPath: 'day' },
    { name: 'by_externalId', keyPath: 'externalId' },
  ],
  doses: [
    { name: 'by_day', keyPath: 'day' },
    { name: 'by_compound', keyPath: 'compoundId' },
    { name: 'by_vial', keyPath: 'vialId' },
  ],
  vials: [{ name: 'by_compound', keyPath: 'compoundId' }],
  protocols: [{ name: 'by_compound', keyPath: 'compoundId' }],
  routines: [{ name: 'by_name', keyPath: 'name' }],
  exercises: [{ name: 'by_name', keyPath: 'name' }],
};

/** Stores keyed by something other than `id`. */
const KEY_PATHS: Partial<Record<StoreName, string>> = {
  metrics: 'day',
  checkins: 'day',
};

const STORE_NAMES: StoreName[] = [
  'foods', 'foodLogs', 'exercises', 'routines', 'programs', 'sessions',
  'runs', 'compounds', 'vials', 'doses', 'protocols', 'metrics', 'checkins', 'raw',
];

let dbPromise: Promise<IDBDatabase> | null = null;

/** Open (and if needed create/upgrade) the database. Idempotent. */
export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, SCHEMA_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const name of STORE_NAMES) {
        if (!db.objectStoreNames.contains(name)) {
          const store = db.createObjectStore(name, { keyPath: KEY_PATHS[name] ?? 'id' });
          for (const idx of INDEXES[name] ?? []) {
            store.createIndex(idx.name, idx.keyPath, { unique: idx.unique ?? false });
          }
        }
      }
      // 'meta' holds settings and sync bookkeeping as plain key/value.
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta');
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // If another tab upgrades the schema, close this handle so it does not
      // block the upgrade and so the next call reopens at the new version.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error('failed to open database'));
    req.onblocked = () => reject(new Error('database upgrade blocked by another tab'));
  });
  return dbPromise;
}

/** Close the handle. Tests use this between cases. */
export async function closeDb(): Promise<void> {
  if (!dbPromise) return;
  const db = await dbPromise;
  db.close();
  dbPromise = null;
}

/** Wrap a request as a promise. */
function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('request failed'));
  });
}

/**
 * Run `fn` inside a transaction and resolve once the transaction COMPLETES.
 * Resolving on request success alone would let a caller believe a write was
 * durable while the transaction was still able to abort.
 */
export async function tx<T>(
  stores: (StoreName | 'meta')[],
  mode: IDBTransactionMode,
  fn: (t: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(stores, mode);
    let result: T;
    let settled = false;
    t.oncomplete = () => { if (!settled) { settled = true; resolve(result); } };
    t.onerror = () => { if (!settled) { settled = true; reject(t.error ?? new Error('transaction failed')); } };
    t.onabort = () => { if (!settled) { settled = true; reject(t.error ?? new Error('transaction aborted')); } };
    (async () => {
      try {
        result = await fn(t);
      } catch (err) {
        if (!settled) { settled = true; reject(err); }
        try { t.abort(); } catch { /* already finishing */ }
      }
    })();
  });
}

// --------------------------------------------------------------------------
// Typed record operations
// --------------------------------------------------------------------------

export async function put<K extends StoreName>(store: K, value: StoreMap[K]): Promise<void> {
  await tx([store], 'readwrite', (t) => promisify(t.objectStore(store).put(value)));
}

export async function putMany<K extends StoreName>(store: K, values: StoreMap[K][]): Promise<void> {
  if (!values.length) return;
  await tx([store], 'readwrite', async (t) => {
    const os = t.objectStore(store);
    for (const v of values) await promisify(os.put(v));
  });
}

export async function get<K extends StoreName>(store: K, key: IDBValidKey): Promise<StoreMap[K] | undefined> {
  return tx([store], 'readonly', (t) => promisify(t.objectStore(store).get(key) as IDBRequest<StoreMap[K] | undefined>));
}

export async function getAll<K extends StoreName>(store: K, query?: IDBKeyRange, count?: number): Promise<StoreMap[K][]> {
  return tx([store], 'readonly', (t) =>
    promisify(t.objectStore(store).getAll(query, count) as IDBRequest<StoreMap[K][]>));
}

export async function del<K extends StoreName>(store: K, key: IDBValidKey): Promise<void> {
  await tx([store], 'readwrite', (t) => promisify(t.objectStore(store).delete(key)));
}

export async function clear(store: StoreName | 'meta'): Promise<void> {
  await tx([store], 'readwrite', (t) => promisify(t.objectStore(store).clear()));
}

export async function count(store: StoreName): Promise<number> {
  return tx([store], 'readonly', (t) => promisify(t.objectStore(store).count()));
}

/** All records whose index value equals `value`. */
export async function byIndex<K extends StoreName>(
  store: K, index: string, value: IDBValidKey | IDBKeyRange,
): Promise<StoreMap[K][]> {
  return tx([store], 'readonly', (t) =>
    promisify(t.objectStore(store).index(index).getAll(value) as IDBRequest<StoreMap[K][]>));
}

/**
 * Records for a closed day range, inclusive.
 *
 * Stores keyed BY day (metrics, checkins) are queried on the primary key;
 * stores that merely carry a day field use their `by_day` index. Routing both
 * through one function keeps callers from having to know which is which --
 * and asking for a `by_day` index on a store already keyed by day throws.
 */
export async function byDayRange<K extends StoreName>(store: K, from: string, to: string): Promise<StoreMap[K][]> {
  const range = IDBKeyRange.bound(from, to);
  if (KEY_PATHS[store] === 'day') return getAll(store, range);
  return byIndex(store, 'by_day', range);
}

/**
 * Read-modify-write under one transaction.
 * Doing this as two separate calls would race against a concurrent sync.
 */
export async function update<K extends StoreName>(
  store: K, key: IDBValidKey, fn: (current: StoreMap[K] | undefined) => StoreMap[K] | undefined,
): Promise<StoreMap[K] | undefined> {
  return tx([store], 'readwrite', async (t) => {
    const os = t.objectStore(store);
    const current = await promisify(os.get(key) as IDBRequest<StoreMap[K] | undefined>);
    const next = fn(current);
    if (next === undefined) {
      if (current !== undefined) await promisify(os.delete(key));
      return undefined;
    }
    await promisify(os.put(next));
    return next;
  });
}

// --------------------------------------------------------------------------
// meta: plain key/value
// --------------------------------------------------------------------------

export async function metaGet<T>(key: string): Promise<T | undefined> {
  return tx(['meta'], 'readonly', (t) => promisify(t.objectStore('meta').get(key) as IDBRequest<T | undefined>));
}

export async function metaSet<T>(key: string, value: T): Promise<void> {
  await tx(['meta'], 'readwrite', (t) => promisify(t.objectStore('meta').put(value, key)));
}

export { STORE_NAMES };
